import { formatDate } from '@/util/formats';
import { useTranslation } from '@/contexts/i18n-context';
import { EncounterResolution, GetTreatmentPlanResponse } from '@/api/api.schemas';
import styles from './encounter-info-header-section.module.css';
import clsx from 'clsx';
import { Button } from '../button';
import { ProviderImage } from '../encounter/provider/provider-image';

interface EncounterInfoHeaderSectionProps {
  treatmentPlan: GetTreatmentPlanResponse;
}

const translationBase = 'visit.treatmentPlan';
const translationBaseEncounterInfo = `${translationBase}.encounterInfo`;

export const treatmentPlanPaths = ['treatment', 'details'] as const;

function HeaderSection({ treatmentPlan }: EncounterInfoHeaderSectionProps) {
  const { t } = useTranslation();

  return (
    <>
      <p className={clsx('overline', styles.date)}>
        {formatDate(treatmentPlan.lastStatusDate, { format: 'user' })}
      </p>

      <h1 className="h3">
        {t(`${translationBaseEncounterInfo}.header`, {
          encounterId: treatmentPlan.encounterId,
        })}
      </h1>
    </>
  );
}

export function EncounterInfoHeaderSection({ treatmentPlan }: EncounterInfoHeaderSectionProps) {
  const { t } = useTranslation();

  const isPatientCancelled =
    treatmentPlan.encounterResolution === EncounterResolution.PatientCancelled;

  if (isPatientCancelled || !treatmentPlan.assignedProvider) {
    return <HeaderSection treatmentPlan={treatmentPlan} />;
  }

  const { canMessage, hasMessages } = treatmentPlan;
  const showMessageButton = canMessage || hasMessages;

  return (
    <>
      <HeaderSection treatmentPlan={treatmentPlan} />
      <div className={styles.divider} />
      <div className={styles.providerSection}>
        <div className={styles.providerSectionContent}>
          <ProviderImage url={treatmentPlan.assignedProvider.img} className={styles.providerImg} />

          <div className={styles.providerInfo}>
            <p className={clsx('body-1', 'bold', styles.providerName)}>
              {treatmentPlan.assignedProvider.name}
              {treatmentPlan.unreadMessages > 0 && (
                <span className={styles.unreadMessages}>{treatmentPlan.unreadMessages}</span>
              )}
            </p>
            <p className={clsx('body-3', styles.messageFromProvider)}>
              {t(`${translationBaseEncounterInfo}.thankYou`)}
            </p>
          </div>
        </div>

        {showMessageButton && (
          <Button
            color="accent"
            as="a"
            to={`/visit/${treatmentPlan.encounterId}/tx-plan/messages`}
            className={styles.messageProviderButton}
            variant={canMessage ? 'filled' : 'outlined'}
          >
            {canMessage
              ? t(`${translationBaseEncounterInfo}.messageProvider`)
              : t(`${translationBaseEncounterInfo}.viewMessages`)}
          </Button>
        )}
      </div>
    </>
  );
}
